// Direct browser client for the HubID identity API (id.hubculture.com).
//
// NOTE ON KEYS: /auth and /user require Public-Key and Private-Key headers.
// This SDK runs in the browser, so both keys are shipped to the client and are
// publicly readable. That is only acceptable because these are HubID
// "browser-safe" keys. Never put a genuinely secret key here.

export class HubLoginError extends Error {
  constructor(message, status, response) {
    super(message);
    this.name = 'HubLoginError';
    this.status = status;
    this.response = response;
  }
}

function authHeaders(config, token) {
  const headers = {
    'Public-Key': config.publicKey,
    'Private-Key': config.privateKey,
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

// Map known HubID error codes to friendly, user-facing text.
const FRIENDLY = {
  invalid_credentials: 'Invalid email or password.',
  token_not_provided: 'Your session could not be verified. Please try again.',
  token_invalid: 'Your session has expired. Please sign in again.',
  token_expired: 'Your session has expired. Please sign in again.',
};

// Pull a human-readable message out of the various error shapes the API uses.
// Real HubID envelope: { status:"error", errors:{error:"invalid_credentials"} | ["msg", ...] }
function extractError(data) {
  if (!data) return null;
  const raw = extractRawError(data);
  if (!raw) return null;
  return FRIENDLY[raw] || raw;
}

function extractRawError(data) {
  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;
  if (data.errors) {
    if (typeof data.errors === 'string') return data.errors;
    if (Array.isArray(data.errors)) return data.errors.join(' ');
    if (typeof data.errors === 'object') {
      const first = Object.values(data.errors)[0]; // {error:"invalid_credentials"} -> "invalid_credentials"
      return Array.isArray(first) ? first[0] : String(first);
    }
  }
  if (data.data && (data.data.errors || data.data.error)) return extractRawError(data.data);
  return null;
}

async function parseJSON(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

// Step 1 of the login: POST /user/checkEmail?client_id=…
//   -> { data: { status: 0|1, message } }   status 1 = account exists
//
// Only Public-Key is required here (no Private-Key). NOTE: this endpoint's
// Access-Control-Allow-Origin is derived from client_id — it returns the origin
// registered for that client. Send the wrong/no client_id and the browser
// blocks the response, which surfaces as a network error.
export async function checkEmail(config, email) {
  const query = config.clientId ? `?client_id=${encodeURIComponent(config.clientId)}` : '';
  let response;
  try {
    response = await fetch(`${config.baseUrl}/user/checkEmail${query}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Public-Key': config.publicKey,
        Accept: 'application/json',
      },
      // deliberately no credentials: the check needs none, and sending them
      // would break against an `Access-Control-Allow-Origin: *` response.
      credentials: 'omit',
      body: JSON.stringify({ email }),
    });
  } catch (_) {
    console.warn(
      '[HubLogin] /user/checkEmail was blocked or unreachable. This endpoint only ' +
        'allows the origin registered for your client_id — check the client-id setting.'
    );
    throw new HubLoginError('Could not reach the login server. Please try again.', 0, null);
  }

  const data = await parseJSON(response);
  const info = data && data.data;
  if (!response.ok || !info) {
    throw new HubLoginError(extractError(data) || 'Could not check that email.', response.status, data);
  }
  return { exists: Number(info.status) === 1, message: info.message || null };
}

// POST /auth  ->  { data: { token, ... } }
export async function authenticate(config, email, password) {
  let response;
  try {
    response = await fetch(`${config.baseUrl}/auth`, {
      method: 'POST',
      headers: { ...authHeaders(config), 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch (err) {
    // fetch rejects (TypeError) on network failure OR a blocked CORS preflight
    throw new HubLoginError(
      'Could not reach the login server. If this persists, HubID may not allow ' +
        'browser requests from this site (CORS).',
      0,
      null
    );
  }

  const data = await parseJSON(response);
  const token = data && data.data && data.data.token;
  if (token) {
    return { token, profile: (data.data && data.data.user) || null, raw: data };
  }

  throw new HubLoginError(
    extractError(data) || 'Invalid email or password.',
    response.status,
    data
  );
}

// PUT /token  ->  { data: { token } }
// Mirrors Ven's getRefreshToken(): exchanges the short-lived /auth access token
// for the longer-lived token that authenticated calls (GET /user) actually accept.
export async function exchangeToken(config, accessToken) {
  try {
    const response = await fetch(`${config.baseUrl}/token`, {
      method: 'PUT',
      headers: authHeaders(config, accessToken),
    });
    const data = await parseJSON(response);
    return (data && data.data && data.data.token) || null;
  } catch (_) {
    return null; // fall back to the original access token
  }
}

// GET /user  ->  { data: { ...profile } }
export async function fetchProfile(config, token) {
  let response;
  try {
    response = await fetch(`${config.baseUrl}/user`, { headers: authHeaders(config, token) });
  } catch (_) {
    return null; // profile is best-effort; login already succeeded
  }
  const data = await parseJSON(response);
  return data && data.data ? data.data : null;
}

// POST /oauth/logout (best-effort)
export async function revoke(config, token) {
  if (!token) return;
  try {
    await fetch(`${config.baseUrl}/oauth/logout`, {
      method: 'POST',
      headers: authHeaders(config, token),
    });
  } catch (_) {
    // ignore — local token is cleared regardless
  }
}
